import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useScripts } from '../hooks/useScripts';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getDomain } from '../utils/getDomain';
import { PLAN_CONFIG } from '../constants/plans';
import { PricingTable } from '../components/PricingTable';
import { checkUserByEmail, hasActiveSubscription } from '../utils/checkUser';
import { submitPurchase, stashSignupPassword } from '../utils/purchase';
import { CheckoutPaymentForm } from '../components/CheckoutPaymentForm';
import { CheckoutPurchaseTerms } from '../components/CheckoutPurchaseTerms';
import './Checkout.css';

function SignupPricing({ onPlanSelect, selectedPlanKey }) {
  const domain = getDomain();
  const siteName = domain.split('.')[0].toUpperCase();
  return (
    <div className="pricing-table animatedParent">
      <div className="container">
        <PricingTable
          title={`Signup to ${siteName} and enjoy your privacy`}
          variant="signup"
          onPlanSelect={onPlanSelect}
          selectedPlanKey={selectedPlanKey}
        />
      </div>
    </div>
  );
}

function Signup() {
  const navigate = useNavigate();
  const domain = getDomain();
  const supportEmail = `contact@${domain}`;
  const siteName = domain.split('.')[0].toUpperCase();
  const [selectedPlanKey, setSelectedPlanKey] = useState('vpn_30_day');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isActiveSubscriptionError, setIsActiveSubscriptionError] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  useDocumentTitle('Sign Up');
  useScripts();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');
    if (plan && PLAN_CONFIG[plan]) {
      setSelectedPlanKey(plan);
    }
    const affid = params.get('affid');
    const clickid = params.get('clickid');
    if (affid != null && affid !== '') {
      localStorage.setItem('vpnnext_affid', affid);
    }
    if (clickid != null && clickid !== '') {
      localStorage.setItem('vpnnext_clickid', clickid);
    }
  }, []);

  const handlePlanSelect = (planKey) => {
    setSelectedPlanKey(planKey);
    setShowPaymentForm(false);
    setPaymentError(null);
    setIsActiveSubscriptionError(false);
  };

  useEffect(() => {
    document.body.classList.add('signup');
    return () => document.body.classList.remove('signup');
  }, []);

  const amount = PLAN_CONFIG[selectedPlanKey] || PLAN_CONFIG.vpn_30_day;

  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    const emailInput = e.target.querySelector('input[type="email"]');
    const passwordInput = e.target.querySelector('input[type="password"]');
    const confirmInput = e.target.querySelectorAll('input[type="password"]')[1];

    const email = emailInput?.value?.trim();
    const password = passwordInput?.value;
    const confirmPassword = confirmInput?.value;

    if (!email || !password || !confirmPassword) {
      setPaymentError('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      setPaymentError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setPaymentError('Password must be at least 6 characters');
      return;
    }

    setPaymentError(null);
    setIsActiveSubscriptionError(false);
    setIsLoadingCheckout(true);

    try {
      const existing = await checkUserByEmail(email);
      if (existing.found && hasActiveSubscription(existing.user)) {
        setIsActiveSubscriptionError(true);
        setPaymentError('This email already has an active plan.');
        return;
      }

      setFormData({ email, password });
      setShowPaymentForm(true);
    } catch (err) {
      setPaymentError(
        err.message || 'Failed to initialize checkout. Please try again.'
      );
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  const handlePay = async (payment) => {
    setPaymentError(null);
    setIsPaying(true);
    try {
      const result = await submitPurchase({
        payment,
        email: formData.email,
        plan: selectedPlanKey,
      });
      if (!result.ok) {
        setPaymentError(result.error || 'Payment failed');
        return;
      }
      if (!result.id) {
        setPaymentError('Payment succeeded but no transaction id was returned.');
        return;
      }
      stashSignupPassword(formData.password);
      const params = new URLSearchParams();
      params.set('transaction_id', result.id);
      navigate(`/success?${params.toString()}`);
    } catch (err) {
      setPaymentError(err.message || 'Payment failed');
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <>
      <SignupPricing onPlanSelect={handlePlanSelect} selectedPlanKey={selectedPlanKey} />

      <div className="bg-form">
        <div className="container">
          <p className="text-center">
            Join us today and start protecting your online privacy.
          </p>
        </div>
      </div>

      <div
        id="signup-form"
        className="form-contact"
        style={{ zIndex: 'auto', marginBottom: '40px' }}
      >
        <h3>Complete your purchase</h3>

        {showPaymentForm ? (
          <div>
            <div
              style={{
                padding: '12px 16px',
                marginBottom: '20px',
                backgroundColor: '#f7fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                color: '#4a5568',
              }}
            >
              <span style={{ fontWeight: 600, color: '#2d3748' }}>Account:</span>{' '}
              {formData.email}
            </div>
            <CheckoutPaymentForm
              email={formData.email}
              error={paymentError}
              disabled={isPaying}
              onPay={handlePay}
            />
            <div
              style={{
                marginTop: '16px',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#8198a6',
                textAlign: 'center',
              }}
            >
              By continuing you agree to our{' '}
              <a href="/tos" style={{ color: '#2563eb' }}>Terms of Service</a>
              {' and '}
              <a href="/privacy-policy" style={{ color: '#2563eb' }}>Privacy Policy</a>.
            </div>
            <CheckoutPurchaseTerms supportEmail={supportEmail} />
            {isActiveSubscriptionError && (
              <p style={{ marginTop: '10px' }}>
                <Link to="/login" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
                  Log in instead →
                </Link>
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                setShowPaymentForm(false);
                setPaymentError(null);
                setIsActiveSubscriptionError(false);
              }}
              style={{
                marginTop: '12px',
                width: '100%',
                padding: '8px',
                background: 'transparent',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#4a5568',
              }}
            >
              Back to form
            </button>
          </div>
        ) : (
          <form onSubmit={handleProceedToPayment}>
            <div className="form-group">
              <label>Enter your email address</label>
              <input type="email" required />
            </div>
            <div className="form-group">
              <label>Choose a password</label>
              <input type="password" required minLength={6} />
            </div>
            <div className="form-group">
              <label>Confirm password</label>
              <input type="password" required minLength={6} />
            </div>

            <div
              className="payment-summary"
              style={{
                marginTop: '30px',
                marginBottom: '20px',
                padding: '20px',
                backgroundColor: '#f7fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
              }}
            >
              <h4
                style={{
                  margin: '0 0 15px 0',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2d3748',
                }}
              >
                Payment Summary
              </h4>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '10px',
                }}
              >
                <span style={{ color: '#4a5568', fontSize: '15px' }}>
                  {amount.label}
                </span>
                <span
                  style={{
                    color: '#2d3748',
                    fontSize: '15px',
                    fontWeight: 500,
                  }}
                >
                  ${amount.display}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '15px',
                  borderTop: '2px solid #e2e8f0',
                  marginTop: '10px',
                }}
              >
                <span
                  style={{
                    color: '#2d3748',
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  Total
                </span>
                <span
                  style={{
                    color: '#2d3748',
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  ${amount.display}
                </span>
              </div>
            </div>

            <div className="form-group text-center" style={{ marginBottom: '20px' }}>
              <button
                className="btn"
                type="submit"
                disabled={isLoadingCheckout}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  fontSize: '16px',
                  fontWeight: 600,
                  backgroundColor: isLoadingCheckout ? '#cbd5e0' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoadingCheckout ? 'not-allowed' : 'pointer',
                }}
              >
                {isLoadingCheckout
                  ? 'Loading...'
                  : `Pay $${amount.display} USD`}
              </button>
              {paymentError && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: isActiveSubscriptionError ? '#fef3c7' : '#fed7d7',
                    color: isActiveSubscriptionError ? '#92400e' : '#c53030',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                >
                  {paymentError}
                  {isActiveSubscriptionError && (
                    <div style={{ marginTop: '10px' }}>
                      <Link
                        to="/login"
                        style={{
                          color: '#2563eb',
                          fontWeight: 600,
                          textDecoration: 'none',
                        }}
                      >
                        Log in instead →
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              className="form-group text-center"
              style={{
                fontSize: '14px',
                color: '#8198a6',
                marginBottom: '20px',
              }}
            >
              <p style={{ margin: 0 }}>
                By submitting this form, you agree to our{' '}
                <a href="/tos" style={{ color: '#2563eb' }}>
                  Terms of Service
                </a>
                {' '}and{' '}
                <a href="/privacy-policy" style={{ color: '#2563eb' }}>
                  Privacy Policy
                </a>
                .
              </p>
              <CheckoutPurchaseTerms
                supportEmail={supportEmail}
                className="signup-disclosures"
              />
            </div>
          </form>
        )}
      </div>

      <div style={{ marginTop: '80px', marginBottom: '40px' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <img
            src="/images/logo.svg"
            alt="VPN Logo"
            style={{ maxWidth: '200px', height: 'auto', marginBottom: '20px' }}
          />
          <h2
            style={{
              marginBottom: '0',
              fontSize: '42px',
              fontWeight: 300,
              color: '#3a3a3a',
            }}
          >
            See what you get with your plan
          </h2>
        </div>
      </div>

      <div
        className="signup-footer"
        style={{
          padding: '80px 0',
          backgroundColor: '#2563eb',
          position: 'relative',
        }}
      >
        <div
          className="container"
          style={{
            position: 'relative',
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '0 40px',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '150px',
            }}
          >
              <div style={{ color: 'white', fontSize: '20px' }}>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    No complicated set-up
                  </strong>
                  After purchase we email your {siteName} VPN credentials and setup
                  guides. Import them on Windows, macOS, Linux, iOS, or Android
                  to connect.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    No limits
                  </strong>
                  We don't restrict use or track your bandwith use. Surf, upload
                  and download all you want.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    Email-based accounts
                  </strong>
                  Sign up with an email address. Billing details are used only to process payment.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    No VPN traffic logs
                  </strong>
                  We don't log the websites you visit over the VPN.
                  Account and payment records are described in our Privacy Policy.
                </div>
              </div>
              <div style={{ color: 'white', fontSize: '20px' }}>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    Lawful use
                  </strong>
                  Use the service in line with our Terms and applicable law.
                  We do not inspect VPN traffic. Copyright infringement and other
                  illegal activity are prohibited.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    Three simultaneous connections
                  </strong>
                  Use up to three devices at the same time with your account.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    Your VPN credentials
                  </strong>
                  We provide {siteName} VPN credentials for your account. Use them
                  with a compatible VPN client on your devices. Setup guides walk
                  you through connecting.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    No kidding
                  </strong>
                  Unlimited speeds. Unlimited server switches.
                </div>
              </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Signup;
